import './select-transfer-submission.scss';

/* eslint-disable no-restricted-globals */
import { Button, ErrorMessage, Fieldset, HintText, Label } from 'nhsuk-react-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { searchByPrimaryRoleIdAndName } from 'services/ods-api';

import { Select } from 'react-functional-select';
import { Select as NhsUkSelect } from 'nhsuk-react-components';
import { TransferProvider } from 'context/TransferContext';
import { safeAccesFullName } from '@util/transfer-submission';
import { setTitle } from '@util/browser';
import { themeConfig } from 'util/select-theme';
import { useNavigate } from 'react-router-dom';

export const destinationOptions = {
    PCN: 'pcn',
    GPPRACTICE: 'gppractice'
};

export const userActionHandlers = {
    transfer: confirmTransferHandler => {
        confirmTransferHandler();
    },
    cancel: cancelHandler => cancelHandler()
};

const SelectTransferSubmission = props => {
    setTitle(`Select destination organisation - ${props.collection.name}`);
    const [orgType, setOrgType] = useState('');
    const [optionsGP, setOptionsGP] = useState([]);
    const [optionsPCN, setOptionsPCN] = useState([]);
    const [orgError, setOrgError] = useState('');
    const [destinationError, setDestinationError] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);
    const delay = 500;
    const selectRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isVisibleConfirmTransfer, setIsVisibleConfirmTransfer] = useState(false);
    const history = useNavigate();
    const getOptionValue = useCallback(option => option.OrgId, []);
    const onOptionChange = useCallback(option => {
        setSelectedOption(option);
        if (option) {
            setDestinationError('');
        }
    }, []);
    const getOptionLabel = useCallback(option => `${option.Name} (${option.OrgId})`, []);

    const onInputChange = useCallback(() => {
        setIsLoading(true);
    }, []);

    const onInputFocusPCN = useCallback(() => setOptionsPCN([]), []);
    const onInputFocusGP = useCallback(() => setOptionsGP([]), []);

    const onSearchChangeGP = useCallback(async value => {
        try {
            if (value.length > 2) {
                const roleId = 'RO177';
                const response = await searchByPrimaryRoleIdAndName(roleId, value);
                selectRef.current?.clearValue();
                const activeOrganisations = response.data.Organisations.filter(
                    org => org.Status !== 'Inactive'
                );
                setOptionsGP(activeOrganisations);
            }
            setIsLoading(false);
        } catch (e) {
            setIsLoading(false);
        }
    }, []);

    const onSearchChangePCN = useCallback(async value => {
        try {
            if (value.length > 2) {
                const roleId = 'RO272';
                const response = await searchByPrimaryRoleIdAndName(roleId, value);
                selectRef.current?.clearValue();
                const rawOrgs = response.data.Organisations || response.data.Organisation;
                const orgs = Array.isArray(rawOrgs) ? rawOrgs : [rawOrgs];

                // Normalise OrgId across the lot
                const normalisedOrgs = orgs.map(org => {
                    const normalisedOrg = { ...org };

                    if (typeof normalisedOrg.OrgId === 'object' && normalisedOrg.OrgId !== null) {
                        normalisedOrg.OrgId = normalisedOrg.OrgId.extension;
                    }

                    return normalisedOrg;
                });

                const activeOrganisations = normalisedOrgs.filter(org => org.Status !== 'Inactive');

                setOptionsPCN(activeOrganisations);
            }
            setIsLoading(false);
        } catch (e) {
            setIsLoading(false);
        }
    }, []);

    const confirmTransferHandler = () => {
        setIsVisibleConfirmTransfer(true);
    };

    const cancelHandler = () => {
        history(`/${props.collection.url_slug}/submission/${props.submissionId}`);
    };

    useEffect(() => {
        async function fetchData() {
            if (orgType === 'RO116') {
                setSelectedOption(null);
                setOptionsGP([]);
                setOrgError('');
            } else {
                setSelectedOption(null);
                setOptionsPCN([]);
                setOrgError('');
            }
        }
        fetchData();
    }, [orgType, setSelectedOption, setOptionsGP, setDestinationError, setOrgError]);

    const selectPCN = (
        <Select
            label="Destination Organisation"
            isClearable
            options={optionsPCN}
            themeConfig={themeConfig}
            ref={selectRef}
            onInputChange={onInputChange}
            inputDelay={delay}
            isLoading={isLoading}
            onSearchChange={onSearchChangePCN}
            onInputFocus={onInputFocusPCN}
            placeholder=""
            noOptionsMsg=""
            onOptionChange={onOptionChange}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            error={destinationError}
        />
    );

    const selectGP = (
        <Select
            label="Destination Organisation"
            isClearable
            options={optionsGP}
            themeConfig={themeConfig}
            ref={selectRef}
            inputDelay={delay}
            isLoading={isLoading}
            onSearchChange={onSearchChangeGP}
            onInputFocus={onInputFocusGP}
            placeholder=""
            noOptionsMsg=""
            onOptionChange={onOptionChange}
            getOptionValue={getOptionValue}
            getOptionLabel={getOptionLabel}
            error={destinationError}
        />
    );

    if (!isVisibleConfirmTransfer) {
        return (
            <div className="nhsuk-grid-row">
                <div className="nhsuk-grid-column-full">
                    <h1>Transfer Person</h1>
                    <Fieldset>
                        <Fieldset.Legend>
                            <div>
                                <Label>Name of Staff</Label>
                                <Label style={{ fontWeight: 'bold' }}>
                                    {safeAccesFullName(props.data)}
                                </Label>
                            </div>
                        </Fieldset.Legend>
                        <div className="nhsuk-u-margin-top-5" error={orgError}>
                            <Label>Organisation Type</Label>
                            <ErrorMessage>{orgError}</ErrorMessage>
                            <HintText>Select an organisation type</HintText>
                            <Fieldset.Legend>
                                <NhsUkSelect
                                    onChange={e => {
                                        const selectedValue = e.target.value;
                                        if (selectedValue === 'GP') {
                                            setOrgType('RO116');
                                        } else if (selectedValue === 'PCN') {
                                            setOrgType('RO272');
                                        } else {
                                            setOrgType('');
                                        }
                                    }}
                                >
                                    <NhsUkSelect.Option value="">Please Select</NhsUkSelect.Option>
                                    <NhsUkSelect.Option key="gp" value="GP">
                                        GP
                                    </NhsUkSelect.Option>
                                    <NhsUkSelect.Option key="pcn" value="PCN">
                                        PCN
                                    </NhsUkSelect.Option>
                                </NhsUkSelect>
                            </Fieldset.Legend>
                        </div>
                        {orgType ? (
                            <Fieldset.Legend>
                                <div className="nhsuk-u-margin-top-5" error={destinationError}>
                                    <Label>Destination Organisation</Label>
                                    <ErrorMessage>{destinationError}</ErrorMessage>
                                    <HintText>
                                        Search for an organisation to transfer this person into
                                    </HintText>

                                    {orgType === 'RO177' || orgType === 'RO116'
                                        ? selectGP
                                        : selectPCN}
                                </div>
                            </Fieldset.Legend>
                        ) : null}
                    </Fieldset>
                    <>
                        <div className="nhsuk-u-margin-top-3">
                            <span>
                                <Button
                                    className="nhsuk-button nhsuk-button--primary"
                                    aria-disabled="false"
                                    type="submit"
                                    onClick={event => {
                                        if (!orgType) {
                                            setOrgError('Please select the Organisation Type');
                                            return;
                                        }
                                        if (!selectedOption) {
                                            setDestinationError(
                                                'Please select the Destination Organisation'
                                            );
                                            return;
                                        }
                                        userActionHandlers.transfer(confirmTransferHandler);
                                    }}
                                >
                                    Transfer
                                </Button>
                            </span>
                            <span className="nhsuk-u-padding-left-3">
                                <Button
                                    className="nhsuk-button nhsuk-button--secondary"
                                    aria-disabled="false"
                                    type="submit"
                                    onClick={() => userActionHandlers.cancel(cancelHandler)}
                                >
                                    Cancel
                                </Button>
                            </span>
                        </div>
                    </>
                </div>
            </div>
        );
    } else {
        return <TransferProvider value={selectedOption}>{props.children}</TransferProvider>;
    }
};

export default SelectTransferSubmission;
